import React, { useState, useEffect, ChangeEvent, useRef } from 'react';
import styles from './coverageStyles.module.css';
import { useSelector, useDispatch } from 'react-redux';
import { CartItem, CoverageOption, OrderSummary, PaymentMethod, Commission } from '../../Models/ProCoverageApiResponse';
import { toast } from 'react-toastify';
import apiService from './dummyApiService';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import CommissionModal from './commissionModal';
import allActions from '../../Store/Actions/AllActions';
import OMSManager from '../../Managers/Oms.manager';
import Constants from '../../Common/Constants';
import Utils from '../../Common/Utils';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import AuthManager from '../../Managers/Auth.manager';
import IdentityManager from '../../Managers/Identity.manager';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OrderManager from '../../Managers/Orders.Manager';
import { IButton, INotificationPopupSettings } from '../../Models/INotificationPopupSettings';
import { CloseIcon } from '../Shared/SharedIcons';
import { NotificationPopup } from '../Shared/NotificationPopup';
import ApiService from '../../Common/ApiService';
import ApiURLS from '../../Common/ApiURLS';
var CryptoJS = require("crypto-js");

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
};

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    fontSize: '14px',
    '& fieldset': {
      borderColor: '#d1d5db',
    },
    '&:hover fieldset': {
      borderColor: '#9ca3af',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
    color: '#6b7280',
    '&.Mui-focused': {
      color: '#3b82f6',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 14px',
  },
}));

interface CoveragePopupProps {
  onClose: () => void;
  onCloseCompleteOrder: () => void;
}

interface ExtendedPaymentMethod extends PaymentMethod {
  isDisabled?: boolean;
  promoCode?: string;
}

interface DisclosureMessageProps {
  disclosureText: string;
}

interface ValuesState {
  CC: string;
  GC: string;
  PLCC: string;
}

const DisclosureMessage: React.FC<DisclosureMessageProps> = ({ disclosureText }) => {
  return (
    <div>
      <p style={{ fontWeight: 'bold' }}>
        This Purchase qualifies for promotional financing. Under this Promotion
      </p>
      <hr style={{ margin: '10px 0' }} />
      <p>{disclosureText}</p>
    </div>
  );
};

const CoveragePopup: React.FC<CoveragePopupProps> = ({ onClose, onCloseCompleteOrder }) => {
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useIsMobile();
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const orderno = urlParams.get('orderno');
  const queryParameterStore = urlParams.get('store');
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [lastSelectedItem, setLastSelectedItem] = useState<string>(''); // Keep this for clarity if needed, though direct selection handles it now.
  const [selectedCoverageMap, setSelectedCoverageMap] = useState<{ [primeLineNumber: string]: CoverageOption }>({});
  const [coverageQuantities, setCoverageQuantities] = useState<{ [primeLineNumber: string]: number }>({});
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<string[]>([]);
  const [paymentAmounts, setPaymentAmounts] = useState<{ [id: string]: string }>({});
  const [showNewPaymentMethodForm, setShowNewPaymentMethodForm] = useState<boolean>(false);
  const [newPaymentMethodType, setNewPaymentMethodType] = useState<string>('');
  const orderLines = useSelector((state: any) => state.CustomerPickupState.orderLines);
  const eligibleLines = orderLines.filter((line: any) => line.IsWarrantyEligible === 'Y');
  const [commissionConfirmSettings, setCommissionConfirmSettings] = useState<INotificationPopupSettings | null>(null);
  const [commissionReviewed, setCommissionReviewed] = useState<boolean>(false);

  const orderDetails = useSelector((state: any) => state.CustomerPickupState.orderDetails);
  const enterpriseCode = useSelector((state: any) => state.CustomerPickupState.enterprise);
  const [coverageOptions, setCoverageOptions] = useState<CoverageOption[]>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<ExtendedPaymentMethod[]>([]);
  const [orderSummary, setOrderSummary] = useState<OrderSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [addWarrantyResponse, setAddWarrantyResponse] = useState([] as any);
  const [existingMethodCvvs, setExistingMethodCvvs] = useState<{ [id: string]: string }>({});
  const [newCardData, setNewCardData] = useState({
    nameOnCard: '',
    cardNumber: '',
    expDate: '',
    saveAgainstCustomer: false,
    isDefaultMethod: false,
    cvv: '',
  });
  const [newOtherPaymentData, setNewOtherPaymentData] = useState({
    reference1: '',
    reference2: '',
    reference3: '',
    amount: '',
    displaySvcNo: '',
    svcNo: '',
    paymentReference5: '',
  });
  const [cardType, setCardType] = useState<string>('');
  const [lineItemCommissions, setLineItemCommissions] = useState<{ [primeLineNumber: string]: Commission[] }>({});
  const [showCommissionModal, setShowCommissionModal] = useState<boolean>(false);
  const [commissionModalPrimeLineNo, setCommissionModalPrimeLineNo] = useState<string>("");
  const [commissionDataFetched, setCommissionDataFetched] = useState<boolean>(false);
  const [paymentDataFetched, setPaymentDataFetched] = useState<boolean>(false);
  const steps = [
    'Add Pro Coverage to Order',
    'Payment & Order Processing',
  ];
  const activeStepMUI = currentStep - 1;
  const dispatch = useDispatch();
  const [selectedItemInLeftPane, setSelectedItemInLeftPane] = useState<string | null>(null);
  const [isCoverageModalOpen, setIsCoverageModalOpen] = useState(false);
  const [modalPrimeLineNumber, setModalPrimeLineNumber] = useState<string | null>(null);
  const [creditOnAccountCharge, setCreditOnAccountCharge] = useState<number>(0);
  const [isCvvRequiredForCC, setIsCvvRequiredForCC] = useState<boolean>(false);
  const [showPayments, setShowPayments] = useState<ValuesState>();
  const [isCvvRequiredForPLCC, setIsCvvRequiredForPLCC] = useState<boolean>(false);
  const [plccPromos, setPlccPromos] = useState<any[]>([]);
  const [selectedPromoCode, setSelectedPromoCode] = useState<{ [methodId: string]: string }>({});
  const [currentPromoSelection, setCurrentPromoSelection] = useState<string>('');
  const [disclosureDetails, setDisclosureDetails] = useState<any | null>(null);
  const [disclosurePopupSettings, setDisclosurePopupSettings] = useState<INotificationPopupSettings | null>(null);
  const [isPromoApplied, setIsPromoApplied] = useState<boolean>(false);
  const [lineSubTotal, setLineSubTotal] = useState<number>()
  const isSaveDisabled = (() => {
    if (submitting) return true;

    switch (newPaymentMethodType) {
      case 'CREDIT_CARD':
        return !newCardData.nameOnCard
          || !newCardData.cardNumber
          || !newCardData.expDate
          || (isCvvRequiredForCC && newCardData.cvv.length !== 3);

      case 'PVT_LBL_CREDIT_CARD':
        return !newCardData.nameOnCard
          || !newCardData.cardNumber
          || (isCvvRequiredForPLCC && newCardData.cvv.length !== 3)
          || !currentPromoSelection;

      case 'GIFT_CARD':
        return !newOtherPaymentData.displaySvcNo ||
          !newOtherPaymentData.paymentReference5;
      default:
        return true;
    }
  })();

  const removeWarrantyService = async () => {
    let listOfWarrantyPrimeLineNumbers = [] as any;
    const filteredCartItems = cartItems.filter(item => selectedItems.includes(item.primeLineNumber));
    filteredCartItems.forEach((cartItem: CartItem) => {
      const currentPrimeLineNumber = cartItem.primeLineNumber
      const warrantyPrimeLineKey = getWarrantyItemPrimeLineKey(currentPrimeLineNumber)
      if (warrantyPrimeLineKey) {
        listOfWarrantyPrimeLineNumbers.push(warrantyPrimeLineKey)
      }
    });

    if (listOfWarrantyPrimeLineNumbers.length > 0) {
      let payload: any;
      if (listOfWarrantyPrimeLineNumbers.length === 1) {
        payload = {
          "Order": {
            "OrderNo": orderno,
            "EnterpriseCode": enterpriseCode,
            "OrderLines": {
              "OrderLine": {
                "PrimeLineNo": listOfWarrantyPrimeLineNumbers[0],
                "Action": "CANCEL"
              }
            }
          }
        };
      } else {
        payload = {
          Order: {
            OrderNo: orderno,
            EnterpriseCode: enterpriseCode,
            OrderLines: listOfWarrantyPrimeLineNumbers.map((p: any) => {
              return {
                OrderLine: {
                  PrimeLineNo: p,
                  Action: "CANCEL"
                }
              };
            })
          },
        };
      }
      dispatch(allActions.actionLoaderActions.showLoader());
      let removedResponse = await OMSManager.GCIRemoveWarrantyService(payload);
      if (!removedResponse.success) {
        setCurrentStep(1);
        dispatch(allActions.actionLoaderActions.hideLoader());
        toast.error('Failed to remove Pro Coverage.', {
          position: "top-center", autoClose: 5000,
          style: { maxWidth: '100vw', wordBreak: 'break-word' }
        });
        return;
      }
      dispatch(allActions.actionLoaderActions.hideLoader());
    }
  }
  useEffect(() => {
    const handleInactivity = () => {
      if (Object.keys(selectedCoverageMap).length > 0) {
        console.log("Step 2 inactive: removing Pro Coverage.");
        removeWarrantyService();
        onClose();
      }
    };

    const handlePageUnload = () => {
      if (Object.keys(selectedCoverageMap).length > 0) {
        console.log("Page closing: removing Pro Coverage.");
        removeWarrantyService();
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      handlePageUnload();
      e.preventDefault();
    };

    const resetInactivityTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      inactivityTimerRef.current = setTimeout(handleInactivity, 600000);
    };

    if (currentStep === 2) {
      const activityEvents: (keyof WindowEventMap)[] = ['mousemove', 'keydown', 'click', 'scroll'];
      activityEvents.forEach(event => window.addEventListener(event, resetInactivityTimer));
      window.addEventListener('beforeunload', handleBeforeUnload);
      resetInactivityTimer();
    }

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      const activityEvents: (keyof WindowEventMap)[] = ['mousemove', 'keydown', 'click', 'scroll'];
      activityEvents.forEach(event => window.removeEventListener(event, resetInactivityTimer));
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentStep, selectedCoverageMap, onClose, removeWarrantyService]);

  useEffect(() => {
    if (loading || submitting) {
      dispatch(allActions.actionLoaderActions.showLoader());
    } else {
      dispatch(allActions.actionLoaderActions.hideLoader());
    }
  }, [loading, submitting, dispatch]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const getConfigurations = async () => {
          let result: any = await ApiService.nomGet(ApiURLS.NOM.getConfiguration.URL);
          if (result.success && result.data && result.data.response && result.data.response.data) {
            return result.data.response.data;
          }
          else {
            console.log("Error fetching configuration:", result);
            return null;
          }
        };
        const configArray = await getConfigurations();
        if (Array.isArray(configArray)) {
          const ccCvvReq = configArray.find(item => item.Key === 'CCCVVREQ');
          const plccCvvReq = configArray.find(item => item.Key === 'PLCCCVVREQ');
          const PROCOVPMDropdowns = configArray.find(item => item.Key === 'PROCOVPMDropdowns');
          if (PROCOVPMDropdowns && PROCOVPMDropdowns.Value) {
            const parsedValue = JSON.parse(PROCOVPMDropdowns.Value);
            setShowPayments(parsedValue);
          }

          setIsCvvRequiredForCC(ccCvvReq?.Value === '1');
          setIsCvvRequiredForPLCC(plccCvvReq?.Value === '1');
        } else {
          throw new Error("Configuration not loaded.");
        }
      } catch (error) {
        toast.warn('Could not load payment configuration. Defaults will be used.', {
          position: "top-center", autoClose: 3000
        });
        setIsCvvRequiredForCC(false);
        setIsCvvRequiredForPLCC(false);
      }
    };

    fetchConfig();
  }, []);

  useEffect(() => {
    if (!orderSummary) {
      return;
    }
    orderSummary.total = Math.round(orderSummary.total * 100) / 100;
    if (selectedPaymentMethods.length === 1) {
      const singleMethodId = selectedPaymentMethods[0];
      setPaymentAmounts({
        [singleMethodId]: orderSummary.total.toFixed(2),
      });

    } else if (selectedPaymentMethods.length > 1) {
      setPaymentAmounts(currentAmounts => {
        const newAmounts = { ...currentAmounts };

        for (const methodId in newAmounts) {
          if (parseFloat(newAmounts[methodId]) === orderSummary.total) {
            newAmounts[methodId] = '';
          }
        }
        return newAmounts;
      });

    } else {
      setPaymentAmounts({});
    }
  }, [selectedPaymentMethods, orderSummary]);

  useEffect(() => {
    const orderNo = orderDetails?.OrderNo || '';
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const cartData = apiService.getCartItemsFromOrderLines(eligibleLines || []);

        const distinctItemsMap = new Map();
        cartData.forEach((item: CartItem) => {
          if (!distinctItemsMap.has(item.primeLineNumber)) {
            distinctItemsMap.set(item.primeLineNumber, item);
          }
        });
        const distinctCartItems = Array.from(distinctItemsMap.values());
        const distinctOrderLines = distinctCartItems.map(item => ({ PrimeLineNo: item.primeLineNumber }));

        let coveragePayload: any = {
          Order: {
            OrderNo: orderNo,
            EnterpriseCode: enterpriseCode,
            OrderLines: {
              OrderLine: distinctOrderLines.length === 1 ? distinctOrderLines[0] : distinctOrderLines
            }
          }
        };

        const coverageResponse = await OMSManager.getCoverageOptions(coveragePayload);

        const coverageData = coverageResponse?.data?.OrderLines?.OrderLine?.flatMap((orderLine: any) => {
          if (Array.isArray(orderLine?.WarrantyList)) {
            let warrantyList: any = []
            orderLine?.WarrantyList.forEach((item: any) => {
              let itemObj = item.Item
              itemObj.primeLineNumber = orderLine.PrimeLineNo
              warrantyList.push(itemObj);
            });
            return warrantyList
          }
          else {
            let items = orderLine?.WarrantyList?.Item;
            items.primeLineNumber = orderLine.PrimeLineNo
            if (!items) return [];
            return Array.isArray(items) ? items : [items];
          }

        }) || [];

        setCoverageOptions(coverageData);
        setCartItems(distinctCartItems);

        try {
          const empId = IdentityManager.getUserProperty(Constants.EmployeeID);
          if (empId) {
            const apiResponse = await AuthManager.GetSuggestionsFromOkta(empId);
            let userName = 'Current User'; // Fallback
            let formattedEmpId = String(empId).padStart(6, '0');

            if (apiResponse.success && apiResponse.data?.data?.length > 0) {
              const userDetails = apiResponse.data.data[0];
              userName = userDetails.displayName;
              formattedEmpId = String(userDetails.employeeId).padStart(6, '0');
            } else {
              toast.warn('Could not verify user details, using defaults for commission.', {
                position: "top-center", autoClose: 3000, style: { maxWidth: '100vw', wordBreak: 'break-word' }
              });
            }

            const defaultCommission: Commission = {
              id: `default-${Date.now()}`,
              employeeNumber: formattedEmpId,
              employeeName: userName,
              percentage: '100',
              commissionType: 'Sales Commission', // Default type
            };

            const initialCommissions: { [primeLineNumber: string]: Commission[] } = {};
            distinctCartItems.forEach(item => {
              initialCommissions[item.primeLineNumber] = [{ ...defaultCommission, id: `default-${item.primeLineNumber}-${Date.now()}` }];
            });
            setLineItemCommissions(initialCommissions);
          } else {
            toast.warn('Could not retrieve current user to set default commission.', {
              position: "top-center", autoClose: 3000, style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
          }
        } catch (error) {
          console.error("Failed to set default commission on load:", error);
          toast.error("An error occurred while setting default user commission.", {
            position: "top-center", autoClose: 5000, style: { maxWidth: '100vw', wordBreak: 'break-word' }
          });
        }

        if (!commissionDataFetched) {
          const commissionPayload = {
            "Order": {
              "OrderNo": orderNo,
              "EnterpriseCode": enterpriseCode,
            }
          };
        }

      } catch (error) {
        console.error('Error loading initial data:', error);
        toast.error('Failed to load data. Please try again.', {
          position: "top-center", autoClose: 5000,
          style: { maxWidth: '100vw', wordBreak: 'break-word' }
        })
      } finally {
        setLoading(false);
      }
    };

    if (orderDetails?.OrderNo) {
      loadInitialData();
    }
  }, [orderLines, orderDetails, enterpriseCode]);

  useEffect(() => {
    const fetchPlccPromos = async () => {
      if (newPaymentMethodType === 'PVT_LBL_CREDIT_CARD' && orderDetails && enterpriseCode) {
        setLoading(true);
        try {
          const payload = {
            "Order": {
              "Customer": {
                "CustomerID": orderDetails.BillToID
              },
              "OrderNo": orderDetails.OrderNo,
              "SellerOrganizationCode": enterpriseCode,
            }
          };
          const response = await OMSManager.GCIGetATGPromoService(payload);
          if (response.success && response.data?.PLCCPromos?.PLCCPromo) {
            const promos = Array.isArray(response.data.PLCCPromos.PLCCPromo)
              ? response.data.PLCCPromos.PLCCPromo
              : [response.data.PLCCPromos.PLCCPromo];
            setPlccPromos(promos);
          } else {
            toast.error('Could not fetch promotional codes.', {
              position: "top-center", autoClose: 3000, style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
            setPlccPromos([]);
          }
        } catch (error) {
          console.error("Error fetching PLCC promos:", error);
          toast.error('An error occurred while fetching promotional codes.', {
            position: "top-center", autoClose: 3000, style: { maxWidth: '100vw', wordBreak: 'break-word' }
          });
          setPlccPromos([]);
        } finally {
          setLoading(false);
        }
      } else {
        setPlccPromos([]);
        setIsPromoApplied(false);
        setCurrentPromoSelection('');
      }
    };

    fetchPlccPromos();
  }, [newPaymentMethodType, orderDetails, enterpriseCode]);


  const handleItemSelect = (primeLineNumber: string) => {
    const item = cartItems.find(i => i.primeLineNumber === primeLineNumber);
    if (item && (!item.isItemEligibleForWarranty || item.isWarrantyAlreadyAdded)) {
      // If item is not eligible or warranty already added, do nothing
      return;
    }
    if (isMobile) {

      if (!selectedItems.includes(primeLineNumber)) {
        setSelectedItems(prev => [...prev, primeLineNumber]);
        if (item) {
          setCoverageQuantities(prev => ({
            ...prev,
            [primeLineNumber]: item.quantityEligibleForPC
          }));
        }
      }
      setModalPrimeLineNumber(primeLineNumber);
      setIsCoverageModalOpen(true);
      return;
    }

    if (selectedItems.includes(primeLineNumber)) {
      // Deselect the item and remove its coverage
      setSelectedItems(prev => prev.filter(id => id !== primeLineNumber));
      setSelectedCoverageMap(prev => {
        const newMap = { ...prev };
        delete newMap[primeLineNumber];
        return newMap;
      });
      // Also remove quantity
      setCoverageQuantities(prev => {
        const newQuantities = { ...prev };
        delete newQuantities[primeLineNumber];
        return newQuantities;
      });
      setSelectedItemInLeftPane(null); // Clear selected item for coverage display
    } else {
      // Select the item and show its coverage options in the left pane
      setSelectedItems(prev => [...prev, primeLineNumber]);
      setSelectedItemInLeftPane(primeLineNumber); // Set this item as the one whose coverage options are displayed
      const item = cartItems.find(i => i.primeLineNumber === primeLineNumber);
      if (item) {
        setCoverageQuantities(prev => ({
          ...prev,
          [primeLineNumber]: item.quantityEligibleForPC // Set quantity to the max available
        }));
      }
    }
  }

  const handleDeclineDisclosure = () => {
    setDisclosurePopupSettings(null);
    toast.warn('To proceed with using your Guitar Center Gear Card with promotional financing, you must accept disclosure terms; otherwise, please either select the non-promotional plan or use an alternate form of payment to continue with your purchase.', {
      position: "top-center", autoClose: 15000, style: { maxWidth: '100vw', wordBreak: 'break-word' }
    });
  };

  const handleAcceptDisclosure = async (details: any): Promise<boolean> => {
    setDisclosurePopupSettings(null);
    setSubmitting(true);
    let success = false;
    try {
      const changeOrderPayload = {
        "Order": {
          "Extn": {
            "GCDisclosureList": {
              "GCDisclosure": {
                "AcceptanceDate": new Date().toISOString(),
                "ChargeTransactionKey": "",
                "Disclosure": details?.disclosureText || "Disclosure Accepted",
                "DisclosureAccepted": "Y",
                "EConsentAccepted": "",
                "IsCustomerInStore": "Y",
                "IsPromotionalPlan": "Y"
              }
            }
          },
          "OrderNo": orderDetails?.OrderNo,
          "EnterpriseCode": enterpriseCode
        }
      };

      const response = await OMSManager.GCIChangeOrderService(changeOrderPayload);
      if (response.success) {
        toast.success("Promotional financing applied successfully!", {
          position: "top-center", autoClose: 3000, style: { maxWidth: '100vw', wordBreak: 'break-word' }
        });
        setIsPromoApplied(true);
        const tempId = 'new_plcc_card';
        setSelectedPromoCode(prev => ({ ...prev, [tempId]: currentPromoSelection }));
        success = true;
      } else {
        toast.error(response.msg || "Failed to apply promotional financing.", {
          position: "top-center", autoClose: 3000, style: { maxWidth: '100vw', wordBreak: 'break-word' }
        });
      }
    } catch (error) {
      console.error("Error in handleAcceptDisclosure:", error);
      toast.error("An error occurred while applying the promotion.", {
        position: "top-center", autoClose: 3000, style: { maxWidth: '100vw', wordBreak: 'break-word' }
      });
    } finally {
      setSubmitting(false);
      return success;
    }
  };


  const handleApplyPromoAndAddPayment = async () => {
    if (!currentPromoSelection) {
      toast.warn("Please select a promotional code.", {
        position: "top-center", autoClose: 3000, style: { maxWidth: '100vw', wordBreak: 'break-word' }
      });
      return;
    }

    const selectedPromo = plccPromos.find(p => p.planCode === currentPromoSelection);
    if (!selectedPromo) {
      toast.error("Invalid promo code selected.", {
        position: "top-center", autoClose: 3000, style: { maxWidth: '100vw', wordBreak: 'break-word' }
      });
      return;
    }

    if (selectedPromo.isSpecialFinancing.toLowerCase() !== 'true') {
      const tempId = 'new_plcc_card';
      setSelectedPromoCode(prev => ({ ...prev, [tempId]: currentPromoSelection }));
      setIsPromoApplied(true);
      await handleAddNewPaymentMethod();
      return;
    }

    setSubmitting(true);
    try {
      const accessToken = IdentityManager.AccessToken;
      if (!accessToken) {
        toast.error("Authentication error. Cannot proceed with payment.");
        setSubmitting(false);
        return;
      }
      const payload = {
        "Order": {
          "OverallTotals": { "SubtotalWithoutTaxes": String(lineSubTotal ?? '0') },
          "PaymentMethods": {
            "PaymentMethod": {
              "CreditCardNo": encryptWithToken(newCardData.cardNumber.replace(/\s/g, ''), accessToken),
              "CreditCardType": "GEAR_CARD",
              "PaymentType": "PVT_LBL_CREDIT_CARD",
              "PromoCode": selectedPromo.planCode,
              "isNew": true,
            }
          },
          "EnterpriseCode": enterpriseCode,
          "OrderNo": orderDetails?.OrderNo,
          "SellerOrganizationCode": enterpriseCode
        }
      };

      const response = await OMSManager.GCIPLCCDisclosureService(payload);
      let disclosureText = ""
      let disclosureSuccess = false
      if (response.success && response.data?.Extn?.GCDisclosureList.GCDisclosure && Array.isArray(response.data?.Extn?.GCDisclosureList.GCDisclosure) && response.data?.Extn?.GCDisclosureList.GCDisclosure.length > 0) {
        if (response.data?.Extn?.GCDisclosureList?.GCDisclosure[0].Status == "SUCCESS") {
          disclosureText = response.data?.Extn?.GCDisclosureList.GCDisclosure[0].Disclosure;
          disclosureSuccess = true
        }
      }
      if (response.success && disclosureSuccess && disclosureText) {
        const newDisclosureDetails = { disclosureText, promoCode: selectedPromo.planCode };
        setDisclosureDetails(newDisclosureDetails);

        const handleAcceptAndThenAdd = async (details: any) => {
          const promoAppliedSuccessfully = await handleAcceptDisclosure(details);
          if (promoAppliedSuccessfully) {
            await handleAddNewPaymentMethod();
          }
        };

        const confirmPopupButtons: IButton[] = [
          { text: "Decline", action: handleDeclineDisclosure, color: Constants.Colors.white, border: Constants.Colors.red, textColor: Constants.Colors.red },
          { text: "Accept", action: () => handleAcceptAndThenAdd(newDisclosureDetails), color: Constants.Colors.red }
        ];

        const popupSettings: INotificationPopupSettings = {
          open: true,
          type: Constants.NotificationPopupType.Info,
          title: "Disclosure",
          msg: "",
          isCustomJSX: true,
          customJSX: <DisclosureMessage disclosureText={disclosureText} />,
          handleClose: handleDeclineDisclosure,
          actions: confirmPopupButtons,
          fullWidth: true,
        };
        setDisclosurePopupSettings(popupSettings);
      } else {
        toast.error("Could not retrieve disclosure information for this promotion.", {
          position: "top-center", autoClose: 3000, style: { maxWidth: '100vw', wordBreak: 'break-word' }
        });
      }
    } catch (error) {
      console.error("Error applying promo code:", error);
      toast.error("An error occurred while applying the promotion.", {
        position: "top-center", autoClose: 3000, style: { maxWidth: '100vw', wordBreak: 'break-word' }
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCoverageChange = (primeLineNumber: string, option: CoverageOption) => {
    setSelectedCoverageMap(prev => ({
      ...prev,
      [primeLineNumber]: option
    }));
    // After selecting coverage, close the relevant UI element.
    if (isMobile) {
      setIsCoverageModalOpen(false);
      setModalPrimeLineNumber(null);
    } else {
      setSelectedItemInLeftPane(null);
    }
  };

  const handleQuantityChange = (primeLineNumber: string, delta: number) => {
    const item = cartItems.find(i => i.primeLineNumber === primeLineNumber);
    if (!item) return;

    const maxQuantity = item.quantityEligibleForPC;
    const currentQuantity = coverageQuantities[primeLineNumber] || 0;
    const newQuantity = currentQuantity + delta;

    if (newQuantity >= 1 && newQuantity <= maxQuantity) {
      setCoverageQuantities(prev => ({
        ...prev,
        [primeLineNumber]: newQuantity
      }));
    }
  };


  const handleRemoveSelectedCoverage = (primeLineNumber: string) => {
    setSelectedCoverageMap(prev => {
      const newMap = { ...prev };
      delete newMap[primeLineNumber];
      return newMap;
    });
    setCoverageQuantities(prev => {
      const newQuantities = { ...prev };
      delete newQuantities[primeLineNumber];
      return newQuantities;
    });
    setSelectedItems(prev => prev.filter(id => id !== primeLineNumber));
    if (selectedItemInLeftPane === primeLineNumber) {
      setSelectedItemInLeftPane(null);
    }
  };


  const calculateSummaryFromLines = (orderLines: any[]): OrderSummary => {
    const filteredCartItems = cartItems.filter(item => selectedItems.includes(item.primeLineNumber));
    let subTotal = selectedItems.reduce((sum, lineNumber) => {
      const coverage = selectedCoverageMap[lineNumber];
      const quantityEligibleForPC = coverageQuantities[lineNumber] || 1;
      if (coverage && !isNaN(Number(coverage.UnitPrice))) {
        return sum + (parseFloat(coverage.UnitPrice) * quantityEligibleForPC);
      }
      return sum;
    }, 0);
    let totalTaxes = 0
    filteredCartItems.forEach((cartItem: CartItem) => {
      const currentPrimeLineNumber = cartItem.primeLineNumber
      const warrantyItemId = selectedCoverageMap[cartItem.primeLineNumber]?.ItemID ?? ""
      const currentPrimeNumberOrderLine = orderLines.filter(x => x.PrimeLineNo == currentPrimeLineNumber)
      if (currentPrimeNumberOrderLine && currentPrimeNumberOrderLine.length > 0) {
        const currentOrderLineKey = currentPrimeNumberOrderLine[0].OrderLineKey
        const currentWarrentyLine = [] as any
        orderLines.forEach((ol: any) => {
          if (ol.DependentOnLineKey && ol.DependentOnLineKey == currentOrderLineKey) {
            currentWarrentyLine.push(ol)
          }
        });
        //FixIt
        currentWarrentyLine.forEach((wi: any) => {
          if (wi.Item && wi.Item.ItemID == warrantyItemId) {// add check for array
            totalTaxes = totalTaxes + wi.LineTaxes.LineTax.reduce((sum: any, tax: any) => sum + parseFloat(tax.Tax), 0);
          }
        });
      }
    });


    return {
      subTotal: subTotal,
      taxes: totalTaxes,
      total: totalTaxes + subTotal,
    };
  };

  const proceedToNextStep = async () => {
    setCommissionReviewed(true);
    setSubmitting(true);
    try {
      // Fetch payment methods only when moving to step 2 and if not already fetched
      if (!paymentDataFetched) {
        const customerID = orderDetails?.BillToID;
        let paymentMethodPayload = {
          orderNo: orderno,
          Customer: {
            CustomerID: customerID,
            OrganizationCode: enterpriseCode,
          }
        };
        const paymentDataResponse = await OMSManager.GCGetPaymentMethod(paymentMethodPayload);
        const paymentData = paymentDataResponse?.data?.CustomerPaymentMethodList?.CustomerPaymentMethod || [];
        const filteredPaymentData = paymentData.filter((method: any) => {
          const allowedGroups = ['CREDIT_ON_ACCOUNT', 'OTHER'];
          if (!allowedGroups.includes(method.PaymentTypeGroup)) {
            return false;
          }
          if (['CREDIT_ON_ACCOUNT', 'OTHER'].includes(method.PaymentTypeGroup)) {
            return method.AvailableAmount !== undefined || method.CurrentBalance !== undefined;
          }
          return true;
        });

        const processedPaymentMethods: ExtendedPaymentMethod[] = filteredPaymentData.map((method: any) => {
          const lastFour = method.DisplayCreditCardNo || method.DisplayCustomerAccountNo || method.DisplaySvcNo;
          let displayText = '';
          let iconUrl = '';
          let isDisabled = false;

          switch (method.PaymentTypeGroup) {
            case 'CREDIT_ON_ACCOUNT':
              const creditAmountCredit = parseFloat(method.AvailableAmount || 0);
              displayText = `Credit On Account  (Balance: $${creditAmountCredit.toFixed(2)})`;
              iconUrl = Constants.CardImagesbase64['account'] || '';
              if (creditAmountCredit <= 0) {
                isDisabled = true;
              }
              break;
            case 'OTHER':
              const creditAmountOther = parseFloat(method.AvailableAmount || 0);
              displayText = `Credit On Account  (Balance: $${creditAmountOther.toFixed(2)})`;
              iconUrl = Constants.CardImagesbase64['account'] || '';
              if (creditAmountOther <= 0) {
                isDisabled = true;
              }
              break;
            default:
              displayText = method.PaymentType;
              iconUrl = Constants.CardImagesbase64['default'];
              break;
          }

          return {
            id: method.PaymentToken || method.CreditCardNo || method.PaymentType || method.DisplaySvcNo,
            type: method.PaymentTypeGroup,
            displayText,
            iconUrl,
            rawPaymentMethodData: method,
            isDisabled: isDisabled
          };
        });

        setPaymentMethods(processedPaymentMethods);
        setPaymentDataFetched(true);
      }

      let coveragePayload: any = {};
      if (selectedItems.length === 1) {
        const primeLineNumber = selectedItems[0];
        const item = selectedCoverageMap[primeLineNumber];
        const quantityEligibleForPC = coverageQuantities[primeLineNumber];
        coveragePayload = {
          Order: {
            Customer: {
              CustomerID: orderDetails?.BillToID || ''
            },
            OrderLines: {
              OrderLine: {
                WarrantyItem: {
                  ItemID: item.ItemID,
                  UnitOfMeasure: item.UnitOfMeasure
                },
                PrimeLineNo: item.primeLineNumber,
                OrderedQty: String(quantityEligibleForPC),
                StoreId: queryParameterStore,
                FulfillmentType: orderDetails?.FulfillmentType || ""
              }
            },
            OrderNo: orderDetails?.OrderNo || '',
            EnterpriseCode: enterpriseCode
          }
        };
      } else {
        coveragePayload = {
          Order: {
            Customer: {
              CustomerID: orderDetails?.BillToID || ''
            },
            OrderLines: {
              OrderLine: selectedItems.map(primeLineNumber => {
                const item = selectedCoverageMap[primeLineNumber];
                const quantityEligibleForPC = coverageQuantities[primeLineNumber];
                return {
                  WarrantyItem: {
                    ItemID: item.ItemID,
                    UnitOfMeasure: item.UnitOfMeasure
                  },
                  PrimeLineNo: item.primeLineNumber,
                  OrderedQty: String(quantityEligibleForPC),
                  StoreId: queryParameterStore,
                  FulfillmentType: orderDetails?.FulfillmentType || ""
                };
              })
            },
            OrderNo: orderDetails?.OrderNo || '',
            EnterpriseCode: enterpriseCode
          }
        };
      }


      const response = await OMSManager.addCoverageToOrder(coveragePayload);
      let lineSubTot = response.data?.Order[0]?.OverallTotals?.LineSubTotal || '0.00';
      setLineSubTotal(parseFloat(lineSubTot));
      if (response && response.success && response.data.Order?.length > 0) {
        setCurrentStep(2);

        let updatedOrderLines = response.data.Order[0].OrderLines.OrderLine;
        if (!Array.isArray(updatedOrderLines)) {
          updatedOrderLines = [updatedOrderLines]
        }
        setAddWarrantyResponse(updatedOrderLines)
        const orderData = response.data.Order[0];
        if (orderData.PaymentMethods && orderData.PaymentMethods.PaymentMethod) {
          const paymentMethodsFromOrder = Array.isArray(orderData.PaymentMethods.PaymentMethod)
            ? orderData.PaymentMethods.PaymentMethod
            : [orderData.PaymentMethods.PaymentMethod];

          const coaPayment = paymentMethodsFromOrder.find(
            (pm: any) => pm.PaymentType === 'CREDIT_ON_ACCOUNT'
          );

          if (coaPayment && coaPayment.MaxChargeLimit) {
            setCreditOnAccountCharge(parseFloat(coaPayment.MaxChargeLimit));
          } else {
            setCreditOnAccountCharge(0);
          }
        } else {
          setCreditOnAccountCharge(0);
        }
        if (orderData.PaymentMethods && orderData.PaymentMethods.PaymentMethod) {
          const newPaymentMethodsFromOrder = Array.isArray(orderData.PaymentMethods.PaymentMethod)
            ? orderData.PaymentMethods.PaymentMethod
            : [orderData.PaymentMethods.PaymentMethod];

          const processedNewMethods = newPaymentMethodsFromOrder.map((method: any): ExtendedPaymentMethod | null => {
            const {
              TotalCharged,
              MaxChargeLimit,
              ...cleanedMethod
            } = method;

            if (cleanedMethod.PaymentType === 'APPLE_PAY' || cleanedMethod.PaymentType === 'PAYPAL') {
              return null;
            }
            const paymentGroup = cleanedMethod.PaymentTypeGroup || cleanedMethod.PaymentType;
            let newMethod: ExtendedPaymentMethod;

            switch (paymentGroup) {
              case 'CREDIT_CARD':
              case 'PVT_LBL_CREDIT_CARD': {
                const cardType = (method.CreditCardType || 'default').toLowerCase();
                const displayText = `${method.CreditCardType || 'Card'} .... ${method.DisplayCreditCardNo || '????'}`;
                const iconUrl = (Constants.CardImagesbase64[cardType as keyof typeof Constants.CardImagesbase64]) || Constants.CardImagesbase64['default'];
                const methodId = method.CreditCardNo || `new_cc_${Date.now()}`;

                newMethod = {
                  id: methodId,
                  type: paymentGroup,
                  displayText,
                  iconUrl,
                  rawPaymentMethodData: cleanedMethod,
                  isDisabled: false,
                  isNew: false
                };
                break;
              }
              default:
                return null;
            }
            return newMethod;
          }).filter((method: any): method is ExtendedPaymentMethod => method !== null);

          if (processedNewMethods.length > 0) {
            setPaymentMethods(prev => {
              const existingIds = new Set(prev.map(p => p.id));
              const uniqueNewMethods = processedNewMethods.filter((p: { id: string; }) => !existingIds.has(p.id));
              return [...uniqueNewMethods, ...prev];
            });

            if (processedNewMethods.length === 1) {
              const newMethodId = processedNewMethods[0].id;
              setSelectedPaymentMethods(prev => {
                const updatedSelection = new Set([...prev, newMethodId]);
                return Array.from(updatedSelection);
              });
            }
          }
        }

        const summary = calculateSummaryFromLines(updatedOrderLines);

        setOrderSummary(summary);
        setCurrentStep(2);
      }
      else {
        toast.error("Failed to add PRO Coverage. Please try again.", {
          position: "top-center", autoClose: 5000,
          style: { maxWidth: '100vw', wordBreak: 'break-word' }
        })
        setOrderSummary({ total: 0, subTotal: 0, taxes: 0 });
      }
    } catch (error) {
      console.error('Error adding PRO Coverage:', error);
      toast.error('An error occurred while adding PRO Coverage.', {
        position: "top-center", autoClose: 5000,
        style: { maxWidth: '100vw', wordBreak: 'break-word' }
      })
      setOrderSummary({ total: 0, subTotal: 0, taxes: 0 })
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmContinue = () => {
    setCommissionConfirmSettings(null);
    proceedToNextStep();
  };

  const handleConfirmCancel = () => {
    setCommissionConfirmSettings(null);
  };

  const handleNextStepClick = async () => {
    if (selectedItems.length === 0) {
      toast.warning("Please select at least one item for PRO Coverage", {
        position: "top-center", autoClose: 5000,
        style: { maxWidth: '100vw', wordBreak: 'break-word' }
      });
      return;
    }
    const missingCoverage = selectedItems.filter(sku => !selectedCoverageMap[sku]);
    if (missingCoverage.length > 0) {
      toast.warning("Please assign a Pro Coverage type to each selected item", {
        position: "top-center", autoClose: 5000,
        style: { maxWidth: '100vw', wordBreak: 'break-word' }
      });
      return;
    }

    if (!commissionReviewed) {
      const confirmPopupButtons: IButton[] = [
        {
          text: "No",
          icon: <CloseIcon />,
          action: handleConfirmCancel,
          color: Constants.Colors.white,
          border: Constants.Colors.red,
          textColor: Constants.Colors.red,
        },
        {
          text: "Yes",
          icon: <CheckCircleOutlineIcon />,
          action: handleConfirmContinue,
          color: Constants.Colors.red
        },
      ];

      const popupSettings: INotificationPopupSettings = {
        open: true,
        type: Constants.NotificationPopupType.Warning,
        title: "Confirm Action",
        msg: "Have you reviewed commissions?",
        handleClose: handleConfirmCancel,
        isCustomJSX: false,
        actions: confirmPopupButtons,
        fullWidth: true,
      };

      setCommissionConfirmSettings(popupSettings);
    } else {
      await proceedToNextStep();
    }
  };

  const handleExistingCvvChange = (methodId: string, cvv: string) => {
    const digitsOnly = cvv.replace(/\D/g, '');
    const formattedCvv = digitsOnly.substring(0, 3);
    setExistingMethodCvvs(prev => ({
      ...prev,
      [methodId]: formattedCvv,
    }));
  };

  const handleBackClick = async () => {
    await removeWarrantyService()
    setCurrentStep(1);
  };
  const closeProveragePopup = async () => {
    if (currentStep === 2) {
      await removeWarrantyService();
    }
    onClose();
  }
  const handleNewCardInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    let formattedValue = value;

    if (type === 'checkbox') {
      setNewCardData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    if (name === 'cardNumber') {
      const digitsOnly = value.replace(/\D/g, '');
      formattedValue = digitsOnly.match(/.{1,4}/g)?.join(' ') || '';
      if (formattedValue.length > 19) {
        formattedValue = formattedValue.substring(0, 19);
      }

      const detectedCardType = Utils.guessCardType(digitsOnly);
      if (detectedCardType === "INVALID_ENTRY" || detectedCardType === "INVALID_GEARCARD_632") {
        toast.error(
          detectedCardType === "INVALID_GEARCARD_632"
            ? "The 632 Gear Card number is no longer valid."
            : "Invalid Entry, please check card number and/or payment type.",
          {
            position: "top-center", autoClose: 5000,
            style: { maxWidth: '100vw', wordBreak: 'break-word' }
          })
        setCardType('');
      } else {
        setCardType(detectedCardType);
      }

    } else if (name === 'nameOnCard') {
      if (formattedValue.length > 25) {
        formattedValue = formattedValue.substring(0, 25);
      }
    }
    else if (name === 'cvv') {
      const digitsOnly = value.replace(/\D/g, '');
      formattedValue = digitsOnly.length > 4 ? digitsOnly.substring(0, 4) : digitsOnly;
    }
    else if (name === 'expDate') {
      const digitsOnly = value.replace(/\D/g, '');

      if (digitsOnly.length > 2) {
        let month = digitsOnly.substring(0, 2);
        let year = digitsOnly.substring(2, 4);

        if (parseInt(month, 10) > 12) {
          month = '12';
        } else if (parseInt(month, 10) === 0) {
          month = '01';
        }

        formattedValue = `${month}/${year}`;
      } else {
        formattedValue = digitsOnly;
      }

      if (formattedValue.length > 5) {
        formattedValue = formattedValue.substring(0, 5);
      }

      const isValid = /^(0[1-9]|1[0-2])\/\d{2}$/.test(formattedValue);
      if (!isValid && formattedValue.length === 5) {
        console.warn('Invalid expiration date format');
      }
    }
    setNewCardData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleOtherPaymentInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewOtherPaymentData(prev => ({ ...prev, [name]: value }));
  };


  const encryptWithToken = (data: string, token: string): string => {
    const key = CryptoJS.SHA256(token);

    const encrypted = CryptoJS.AES.encrypt(data, key, {
      mode: CryptoJS.mode.ECB, //ECB doesnt require an encryption IV to be sent
      padding: CryptoJS.pad.Pkcs7
    });

    return encrypted.toString();
  };

  const handleNewPaymentMethodTypeChange = (event: SelectChangeEvent<string>) => {
    setNewPaymentMethodType(event.target.value as string);
    setNewCardData({
      nameOnCard: '',
      cardNumber: '',
      expDate: '',
      saveAgainstCustomer: false,
      isDefaultMethod: false,
      cvv: ''
    });
    setNewOtherPaymentData({
      reference1: '',
      reference2: '',
      reference3: '',
      amount: '',
      displaySvcNo: '',
      svcNo: '',
      paymentReference5: ''
    });
    setCardType('');
  };

  const handlePaymentMethodSelect = (methodId: string) => {
    setSelectedPaymentMethods(prev => {
      if (prev.includes(methodId)) {
        setPaymentAmounts(current => {
          const newAmounts = { ...current };
          delete newAmounts[methodId];
          return newAmounts;
        });
        return prev.filter(id => id !== methodId);
      } else {
        const method = paymentMethods.find(m => m.id === methodId);
        // Only initialize amount for new 'OTHER' type payments if they are meant to be single-use with a specific amount
        // For existing methods, or credit cards, the user will input or it will be auto-filled if only one payment.
        if (method?.isNew && (method.type === 'GIFT_CARD' || method.type === 'CREDIT_ON_ACCOUNT')) {
          setPaymentAmounts(current => ({
            ...current,
            [methodId]: method.rawPaymentMethodData.RequestedAmount || (orderSummary?.total.toFixed(2) || '0.00')
          }));
        }
        return [...prev, methodId];
      }
    });
  };

  const handlePaymentAmountChange = (methodId: string, amount: string) => {
    const numericAmount = amount.replace(/[^0-9.]/g, '');
    const parts = numericAmount.split('.');
    const finalAmount = parts.length > 1 ? parts[0] + '.' + parts.slice(1).join('') : parts[0];

    setPaymentAmounts(prev => ({
      ...prev,
      [methodId]: finalAmount,
    }));
  };

  const handleAddNewPaymentMethod = async () => {

    setSubmitting(true);
    dispatch(allActions.actionLoaderActions.showLoader());

    try {
      const accessToken = IdentityManager.AccessToken;
      if (!accessToken) {
        toast.error("Authentication error. Cannot add payment method.");
        setSubmitting(false);
        dispatch(allActions.actionLoaderActions.hideLoader());
        return;
      }
      let newMethodForState: ExtendedPaymentMethod; // Use ExtendedPaymentMethod
      let newMethodRawData: any;

      switch (newPaymentMethodType) {
        case 'CREDIT_CARD':
          if (!newCardData.nameOnCard || !newCardData.cardNumber || !newCardData.expDate || isCvvRequiredForCC && !newCardData.cvv) {
            toast.error('Please fill all new card details.', {
              position: "top-center", autoClose: 5000,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            })
            return;
          }
          if (isCvvRequiredForCC && newCardData.cvv.length !== 3) {
            toast.error('A valid 3-digit CVV is required.');
            setSubmitting(false);
            dispatch(allActions.actionLoaderActions.hideLoader());
            return;
          }

          const cardNumberDigits = newCardData.cardNumber.replace(/\s/g, '');
          const finalCardType = Utils.guessCardType(cardNumberDigits);

          if (finalCardType === 'GEAR_CARD') {
            toast.error("This appears to be a Gear Card. Please use the 'Private Label Credit Card' option instead.", {
              position: "top-center", autoClose: 5000,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
            return;
          }

          let isValidLength = false;
          let expectedLengthMessage = '';
          let isValid = true;

          switch (finalCardType) {
            case 'VISA':
              isValidLength = [13, 16, 19].includes(cardNumberDigits.length);
              expectedLengthMessage = '13, 16, or 19 digits';
              break;
            case 'MASTERCARD':
            case 'GEAR_CARD':
            case 'ESSENTIALS_CARD':
              isValidLength = cardNumberDigits.length === 16;
              expectedLengthMessage = '16 digits';
              break;
            case 'AMEX':
              isValidLength = cardNumberDigits.length === 15;
              expectedLengthMessage = '15 digits';
              break;
            case 'DISCOVER':
              isValidLength = cardNumberDigits.length >= 16 && cardNumberDigits.length <= 19;
              expectedLengthMessage = '16 to 19 digits';
              break;
            case 'DINERS_CLUB':
            case 'JCB':
              isValid = false;
              break;
            default:
              isValidLength = cardNumberDigits.length >= 13 && cardNumberDigits.length <= 19;
              expectedLengthMessage = '13 to 19 digits';
              break;
          }

          if (!isValid) {
            toast.error("This Card type is not used anymore. Please use any other Card to proceed.", {
              position: "top-center", autoClose: 5000,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            })
            return
          }

          if (!isValidLength) {
            toast.error(`Invalid card length for ${finalCardType}. Expected ${expectedLengthMessage}.`, {
              position: "top-center", autoClose: 5000,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            })
            return;
          }

          const expDateParts = newCardData.expDate.split('/');
          if (expDateParts.length !== 2 || !/^(0[1-9]|1[0-2])$/.test(expDateParts[0]) || !/^\d{2}$/.test(expDateParts[1])) {
            toast.error('Invalid expiration date format. Please use MM/YY.', {
              position: "top-center", autoClose: 5000,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
            return;
          }

          const expMonth = parseInt(expDateParts[0], 10);
          const expYear = parseInt(expDateParts[1], 10) + 2000;
          const today = new Date();
          const currentYear = today.getFullYear();
          const currentMonth = today.getMonth() + 1;

          if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
            toast.error('The expiration date cannot be in the past.', {
              position: "top-center", autoClose: 5000,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
            return;
          }

          if (["INVALID_ENTRY", "INVALID_GEARCARD_632", "UNKNOWN"].includes(finalCardType)) {
            toast.error("Invalid card number or payment type. Please check your entry.", {
              position: "top-center", autoClose: 5000,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
            return;
          }
          // ### END: Integrated Validations ###

          const nameParts = newCardData.nameOnCard.split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
          const formattedExpDate = `${expDateParts[0]}/20${expDateParts[1]}`;

          newMethodRawData = {
            CreditCardExpDate: formattedExpDate,
            CreditCardName: newCardData.nameOnCard,
            CreditCardNo: encryptWithToken(cardNumberDigits, accessToken),
            CreditCardType: finalCardType.toUpperCase(),
            DisplayCreditCardNo: newCardData.cardNumber.slice(-4),
            DisplayPrimaryAccountNo: newCardData.cardNumber.slice(-4),
            FirstName: firstName,
            LastName: lastName,
            PaymentReference1: "",
            PaymentType: newPaymentMethodType,
            PaymentTypeGroup: newPaymentMethodType,
            IsDefaultMethod: "N",
            SaveAgainstCustomer: "N",
            RequestedAmount: "",
            DisplayCustomerAccountNo: "",
            DisplayPaymentReference1: "",
            MiddleName: "",
            PaymentReference3: "",
            SecureAuthenticationCode: newCardData.cvv ? newCardData.cvv : "",
          };

          newMethodForState = {
            id: `new_cc_${Date.now()}`,
            type: newPaymentMethodType,
            displayText: `${finalCardType.toUpperCase()} .... ${newCardData.cardNumber.slice(-4)}`,
            iconUrl: (Constants.CardImagesbase64[finalCardType.toLowerCase() as keyof typeof Constants.CardImagesbase64]) || Constants.CardImagesbase64['default'],
            isNew: true,
            rawPaymentMethodData: newMethodRawData,
            isDisabled: false
          };
          break;

        case 'PVT_LBL_CREDIT_CARD':
          if (!newCardData.nameOnCard || !newCardData.cardNumber || (isCvvRequiredForPLCC && !newCardData.cvv)) {
            toast.error('Please fill all new card details.', {
              position: "top-center", autoClose: 5000,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
            return;
          }

          if (isCvvRequiredForPLCC && newCardData.cvv.length !== 3) {
            toast.error('A valid 3-digit CVV is required.');
            setSubmitting(false);
            dispatch(allActions.actionLoaderActions.hideLoader());
            return;
          }

          const finalCardTypePLCC = Utils.validatePLCCNumber(newCardData.cardNumber.replace(/\s/g, ''));
          if (["INVALID_ENTRY", "INVALID_GEARCARD_632", "INVALID"].includes(finalCardTypePLCC)) {
            toast.error("Invalid card number for Private Label Card. Please check your entry.", {
              position: "top-center", autoClose: 5000,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
            return;
          }

          const namePartsPLCC = newCardData.nameOnCard.split(' ');
          const firstNamePLCC = namePartsPLCC[0] || '';
          const lastNamePLCC = namePartsPLCC.length > 1 ? namePartsPLCC.slice(1).join(' ') : '';
          const appliedPromoCode = currentPromoSelection;
          newMethodRawData = {
            CreditCardName: newCardData.nameOnCard,
            CreditCardNo: encryptWithToken(newCardData.cardNumber.replace(/\s/g, ''), accessToken),
            CreditCardType: finalCardTypePLCC.toUpperCase(),
            DisplayCreditCardNo: newCardData.cardNumber.slice(-4),
            DisplayPrimaryAccountNo: newCardData.cardNumber.slice(-4),
            FirstName: firstNamePLCC,
            LastName: lastNamePLCC,
            PaymentType: newPaymentMethodType,
            PaymentTypeGroup: newPaymentMethodType,
            IsDefaultMethod: "N",
            RequestedAmount: "",
            SecureAuthenticationCode: newCardData.cvv ? newCardData.cvv : "",
            PaymentReference8: appliedPromoCode,
            PaymentReference9: appliedPromoCode,
          };

          newMethodForState = {
            id: `new_cc_${Date.now()}`,
            type: newPaymentMethodType,
            displayText: `${finalCardTypePLCC.toUpperCase()} .... ${newCardData.cardNumber.slice(-4)}`,
            promoCode: appliedPromoCode,
            iconUrl: (Constants.CardImagesbase64[finalCardTypePLCC.toLowerCase() as keyof typeof Constants.CardImagesbase64]) || Constants.CardImagesbase64['default'],
            isNew: true,
            rawPaymentMethodData: newMethodRawData,
            isDisabled: false
          };
          break;

        case 'GIFT_CARD':
          if (!newOtherPaymentData.displaySvcNo || !newOtherPaymentData.paymentReference5) {
            toast.error('Please provide the full Gift Card number and PIN.', {
              position: "top-center", autoClose: 5000,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
            return;
          }

          const giftCardNumber = newOtherPaymentData.displaySvcNo.replace(/\s/g, '');
          const giftCardPin = newOtherPaymentData.paymentReference5.replace(/\s/g, '');

          // Gift Card and PIN length validation 
          if (!/^\d{16}$/.test(giftCardNumber)) {
            toast.error('Invalid Gift Card number. It must be 16 digits.', {
              position: "top-center", autoClose: 5000,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
            return;
          }
          if (!/^\d{8}$/.test(giftCardPin)) {
            toast.error('Invalid PIN. It must be 8 digits.', {
              position: "top-center", autoClose: 5000,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
            return;
          }

          newMethodRawData = {
            DisplaySvcNo: giftCardNumber.slice(-4),
            IsCorrection: "Y",
            Operation: "Manage",
            PaymentReference5: giftCardPin,
            PaymentType: "GIFT_CARD",
            RequestedAmount: newOtherPaymentData.amount,
            SvcNo: encryptWithToken(giftCardNumber, accessToken),
          };

          newMethodForState = {
            id: `new_gift_${Date.now()}`,
            type: newPaymentMethodType,
            displayText: `Gift Card .... ${giftCardNumber.slice(-4)}`,
            iconUrl: Constants.CardImagesbase64['gift_card'] || '',
            isNew: true,
            rawPaymentMethodData: newMethodRawData,
            isDisabled: false
          };
          break;

        default:
          toast.error('Invalid payment method selected.', {
            position: "top-center", autoClose: 5000,
            style: { maxWidth: '100vw', wordBreak: 'break-word' }
          });
          return;
      }

      setPaymentMethods(prev => [newMethodForState, ...prev]);
      setSelectedPaymentMethods(prev => [...prev, newMethodForState.id]);
      if (newCardData.cvv && (newPaymentMethodType === 'CREDIT_CARD' || newPaymentMethodType === 'PVT_LBL_CREDIT_CARD')) {
        setExistingMethodCvvs(prev => ({
          ...prev,
          [newMethodForState.id]: newCardData.cvv
        }));
      }
      if (newMethodForState.rawPaymentMethodData.RequestedAmount) {
        setPaymentAmounts(prev => ({
          ...prev,
          [newMethodForState.id]: newMethodForState.rawPaymentMethodData.RequestedAmount
        }));
      } else if (orderSummary) {
        setPaymentAmounts(prev => ({
          ...prev,
          [newMethodForState.id]: orderSummary.total.toFixed(2)
        }));
      }


      setShowNewPaymentMethodForm(false);
      setNewPaymentMethodType('');
      setNewCardData({ nameOnCard: '', cardNumber: '', expDate: '', saveAgainstCustomer: false, isDefaultMethod: false, cvv: '' });
      setNewOtherPaymentData({ reference1: '', reference2: '', reference3: '', amount: '', displaySvcNo: '', svcNo: '', paymentReference5: '' });
      setCardType('');
      setPlccPromos([]);
      setCurrentPromoSelection('');
      setIsPromoApplied(false);
      setDisclosureDetails(null);
    } catch (error) {
      console.error('Error adding new payment method to state:', error);
      toast.error('Failed to add new payment method to list.', {
        position: "top-center", autoClose: 5000,
        style: { maxWidth: '100vw', wordBreak: 'break-word' }
      })
    } finally {
      setSubmitting(false);
      dispatch(allActions.actionLoaderActions.hideLoader());
    }
  };

  const handleUpdateCommissionClick = async (primeLineNumber: string) => {
    setCommissionModalPrimeLineNo(primeLineNumber);
    setShowCommissionModal(true);
  };
  const getWarrantyItemPrimeLineKey = (mainPrimeLineKey: string) => {
    let warrantyPrimeLineKey = "";
    const warrantyItemId = selectedCoverageMap[mainPrimeLineKey]
    const currentPrimeNumberOrderLine = addWarrantyResponse.filter((x: any) => x.PrimeLineNo == mainPrimeLineKey)
    if (currentPrimeNumberOrderLine && currentPrimeNumberOrderLine.length > 0) {
      const currentOrderLineKey = currentPrimeNumberOrderLine[0].OrderLineKey
      const currentWarrentyLine = [] as any
      addWarrantyResponse.forEach((ol: any) => {
        if (ol.DependentOnLineKey && ol.DependentOnLineKey == currentOrderLineKey) {
          currentWarrentyLine.push(ol)
        }
      });
      currentWarrentyLine.forEach((wi: any) => {
        if (wi.Item && wi.Item.ItemID == warrantyItemId.ItemID && wi.MinLineStatus == "1100") {
          warrantyPrimeLineKey = wi.PrimeLineNo
        }
      });
    }
    return warrantyPrimeLineKey
  }


  const handleCloseCommissionModal = async (updatedCommissions: Commission[], primeLineNumber?: string) => {
    setShowCommissionModal(false);
    setCommissionModalPrimeLineNo("");

    if (primeLineNumber) {
      setLineItemCommissions(prev => ({
        ...prev,
        [primeLineNumber]: updatedCommissions,
      }));
    } else {

    }

  };


  const handleCompleteOrder = async () => {
    if (selectedPaymentMethods.length === 0) {
      toast.warning('Please select at least one payment method.', {
        position: "top-center", autoClose: 5000,
        style: { maxWidth: '100vw', wordBreak: 'break-word' }
      });
      return;
    }

    let totalPaidAmount = 0;
    const customerID = orderDetails?.BillToID;
    const orderNo = orderDetails?.OrderNo;
    const paymentPayloads: any[] = [];
    for (const methodId of selectedPaymentMethods) {
      const selectedPaymentDetails = paymentMethods.find(pm => pm.id === methodId);

      if (selectedPaymentDetails?.isDisabled) {
        toast.warning(`Cannot use disabled payment method: ${selectedPaymentDetails.displayText}`, {
          position: "top-center", autoClose: 5000,
          style: { maxWidth: '100vw', wordBreak: 'break-word' }
        });
        return;
      }

      const amount = parseFloat(paymentAmounts[methodId] || '0');
      if (isNaN(amount) || amount <= 0) {
        toast.warning(`Please enter a valid amount for selected payment method.`, {
          position: "top-center", autoClose: 5000,
          style: { maxWidth: '100vw', wordBreak: 'break-word' }
        })
        return;
      }

      if (selectedPaymentDetails && (selectedPaymentDetails.type === 'CREDIT_CARD' || selectedPaymentDetails.type === 'PVT_LBL_CREDIT_CARD')) {
        const isRequired = selectedPaymentDetails.type === 'CREDIT_CARD' ? isCvvRequiredForCC : isCvvRequiredForPLCC;
        const cvv = existingMethodCvvs[methodId] || '';
        if (isRequired && cvv.length !== 3) {
          toast.error(`A valid 3-digit CVV is required for ${selectedPaymentDetails.displayText}.`, {
            position: "top-center", autoClose: 5000,
            style: { maxWidth: '100vw', wordBreak: 'break-word' }
          });
          return;
        }
      }

      let finalRequestedAmount = amount;
      if (selectedPaymentDetails?.id === 'CREDIT_ON_ACCOUNT' && selectedPaymentDetails?.type === 'OTHER' && creditOnAccountCharge > 0) {
        finalRequestedAmount += creditOnAccountCharge;
      }

      if (selectedPaymentDetails?.type === 'CREDIT_ON_ACCOUNT' || selectedPaymentDetails?.type === 'OTHER') {
        const availableAmount = parseFloat(selectedPaymentDetails.rawPaymentMethodData.AvailableAmount || '0');
        if (amount > availableAmount) {
          toast.warning(`The amount for Credit On Account ($${amount.toFixed(2)}) exceeds the available balance ($${availableAmount.toFixed(2)}). Please adjust the amount.`, {
            position: "top-center", autoClose: 5000,
            style: { maxWidth: '100vw', wordBreak: 'break-word' }
          })
          return;
        }
      }

      totalPaidAmount += amount;

      const paymentMethodPayloadForProcessing = { ...selectedPaymentDetails?.rawPaymentMethodData };
      paymentMethodPayloadForProcessing.RequestedAmount = String(finalRequestedAmount);
      paymentMethodPayloadForProcessing.IsNew = !!selectedPaymentDetails?.isNew;

      if (selectedPaymentDetails && (selectedPaymentDetails.type === 'CREDIT_CARD' || selectedPaymentDetails.type === 'PVT_LBL_CREDIT_CARD')) {
        const cvv = selectedPaymentDetails.isNew ? newCardData.cvv : existingMethodCvvs[methodId];
        if (cvv) {
          paymentMethodPayloadForProcessing.SecureAuthenticationCode = cvv
        }
      }

      if (selectedPaymentDetails?.type === 'CREDIT_CARD') {
        paymentMethodPayloadForProcessing.CreditCardNo = paymentMethodPayloadForProcessing.CreditCardNo;
        paymentMethodPayloadForProcessing.CreditCardType = paymentMethodPayloadForProcessing.CreditCardType;
        paymentMethodPayloadForProcessing.CreditCardName = paymentMethodPayloadForProcessing.CreditCardName;
        paymentMethodPayloadForProcessing.FirstName = paymentMethodPayloadForProcessing.FirstName;
        paymentMethodPayloadForProcessing.LastName = paymentMethodPayloadForProcessing.LastName;
        paymentMethodPayloadForProcessing.PaymentReference2 = paymentMethodPayloadForProcessing.PaymentReference2;
        paymentMethodPayloadForProcessing.CreditCardExpDate = paymentMethodPayloadForProcessing.CreditCardExpDate;
        paymentMethodPayloadForProcessing.Operation = "Manage";
        paymentMethodPayloadForProcessing.SaveAgainstCustomer = "N";
      }
      else if (selectedPaymentDetails?.type === 'PVT_LBL_CREDIT_CARD') {
        paymentMethodPayloadForProcessing.CreditCardNo = paymentMethodPayloadForProcessing.CreditCardNo;
        paymentMethodPayloadForProcessing.CreditCardType = "GEAR_CARD";
        paymentMethodPayloadForProcessing.CreditCardName = paymentMethodPayloadForProcessing.CreditCardName;
        paymentMethodPayloadForProcessing.FirstName = paymentMethodPayloadForProcessing.FirstName;
        paymentMethodPayloadForProcessing.LastName = paymentMethodPayloadForProcessing.LastName;
        paymentMethodPayloadForProcessing.Operation = "Manage";
        paymentMethodPayloadForProcessing.SaveBillToOnOrder = "N";
        paymentMethodPayloadForProcessing.SaveAgainstCustomer = "N";
      }
      else if (selectedPaymentDetails?.type === 'GIFT_CARD') {
        paymentMethodPayloadForProcessing.DisplaySvcNo = paymentMethodPayloadForProcessing.DisplaySvcNo;
        paymentMethodPayloadForProcessing.SvcNo = paymentMethodPayloadForProcessing.SvcNo;
        paymentMethodPayloadForProcessing.PaymentReference5 = paymentMethodPayloadForProcessing.PaymentReference5;
        paymentMethodPayloadForProcessing.Operation = "Manage";
        paymentMethodPayloadForProcessing.IsCorrection = "Y";
      } else if (selectedPaymentDetails?.type === 'CREDIT_ON_ACCOUNT' || selectedPaymentDetails?.type === 'OTHER') {
        paymentMethodPayloadForProcessing.PaymentReference1 = customerID; // Ensure this is the BillToID/CustomerID
        paymentMethodPayloadForProcessing.Operation = "Manage";
        paymentMethodPayloadForProcessing.IsCorrection = "Y";
      }

      paymentPayloads.push(paymentMethodPayloadForProcessing);
    }

    if (orderSummary && Math.abs(totalPaidAmount - orderSummary.total) > 0.01) {
      toast.warning(
        `Total payment amount ($${totalPaidAmount.toFixed(2)}) does not match the order total ($${orderSummary.total.toFixed(2)}). Please adjust the payment amounts.`, {
        position: "top-center", autoClose: 5000,
        style: { maxWidth: '100vw', wordBreak: 'break-word' }
      })
      return;
    }

    setSubmitting(true);
    dispatch(allActions.actionLoaderActions.showLoader());

    try {
      const processPaymentPayload = {
        Order: {
          OrderNo: orderNo,
          EnterpriseCode: enterpriseCode,
          Customer: { CustomerID: customerID },
          PaymentMethods: paymentPayloads.length === 1 ?
            { PaymentMethod: paymentPayloads[0] } :
            { PaymentMethod: paymentPayloads }
        }
      };

      console.log('New payment methods detected. Sending GCIProcessPaymentService payload...');
      const paymentResult = await OMSManager.addPaymentMethodToCustomer(processPaymentPayload);

      if (!paymentResult.success) {
        toast.error('Failed to save the new payment method. Please try again.', {
          position: "top-center", autoClose: 5000,
          style: { maxWidth: '100vw', wordBreak: 'break-word' }
        })
        throw new Error("Failed to add new payment method.");
      }

      // 3. Update Commissions (if any)
      let finalCommissionListPayload = [] as any;
      Object.keys(lineItemCommissions).forEach((lineKey: any) => {
        let currentCommissionList = lineItemCommissions[lineKey];
        let warrantyPrimeLineNumber = getWarrantyItemPrimeLineKey(lineKey);

        if (currentCommissionList && warrantyPrimeLineNumber) {
          let GCOrderLineCommissionList;
          if (currentCommissionList.length === 1) {
            let comm = currentCommissionList[0];
            GCOrderLineCommissionList = {
              GCOrderLineCommission: {
                AllocationPercentage: String(comm.percentage),
                CommissionType: comm.commissionType,
                IsOrderCommissionOverriden: "Y",
                UserID: comm.employeeNumber,
                UserName: comm.employeeName,
              }
            };
          } else if (currentCommissionList.length > 1) {
            let currentList = currentCommissionList.map(comm => ({
              AllocationPercentage: String(comm.percentage),
              CommissionType: comm.commissionType,
              IsOrderCommissionOverriden: "Y",
              UserID: comm.employeeNumber,
              UserName: comm.employeeName,
            }));
            GCOrderLineCommissionList = {
              GCOrderLineCommission: currentList
            };
          }

          if (GCOrderLineCommissionList) {
            let orderLine = {
              PrimeLineNo: warrantyPrimeLineNumber,
              GCOrderLineCommissionList: GCOrderLineCommissionList
            };
            finalCommissionListPayload.push(orderLine);
          }
        }
      });

      if (finalCommissionListPayload.length > 0) {
        let commissionUpdatePayload = {
          "Order": {
            "OrderNo": orderNo,
            "EnterpriseCode": enterpriseCode,
            "Customer": { "CustomerID": customerID },
            "OrderLines": {
              "OrderLine": finalCommissionListPayload.length === 1
                ? finalCommissionListPayload[0]
                : finalCommissionListPayload
            }
          }
        }
        console.log('Sending GCIUpdateCommissionService payload:', JSON.stringify(commissionUpdatePayload, null, 2));
        const commissionUpdateResponse = await OMSManager.GCIUpdateCommissionService(commissionUpdatePayload);

        if (!commissionUpdateResponse.success) {
          toast.error(commissionUpdateResponse.msg || 'Failed to update commissions. Please try again.', {
            position: "top-center", autoClose: 5000,
            style: { maxWidth: '100vw', wordBreak: 'break-word' }
          });
          throw new Error("Commission update failed");
        }
      } else {
        console.log('No line-level commissions to update, skipping GCIUpdateCommissionService call.');
      }

      // 4. Complete the Order
      const completeOrderPayload = {
        Order: {
          OrderNo: orderNo,
          EnterpriseCode: enterpriseCode,
          Customer: { CustomerID: customerID },
          PaymentMethods: paymentPayloads.length === 1
            ? { PaymentMethod: paymentPayloads[0] }
            : { PaymentMethod: paymentPayloads }
        }
      };

      // const completeOrderPayload = {
      //   Order: {
      //     OrderNo: orderNo,
      //     EnterpriseCode: enterpriseCode,
      //     Customer: { CustomerID: customerID }
      //   }
      // };
      console.log('Sending GCICompleteOrderService payload:', JSON.stringify(completeOrderPayload, null, 2));
      const result = await OMSManager.GCICompleteOrderService(completeOrderPayload);
      console.log('GCICompleteOrderService response:', JSON.stringify(result));
      const typedResult = result as { success: boolean; orderId?: string; error?: string, data?: any };

      if (typedResult.success) {
        const errorData = result.data;
        let paymentFailed = false;

        if (errorData?.ChargeTransactionDetails?.ChargeTransactionDetail) {
          const chargeDetails = Array.isArray(errorData.ChargeTransactionDetails.ChargeTransactionDetail)
            ? errorData.ChargeTransactionDetails.ChargeTransactionDetail
            : [errorData.ChargeTransactionDetails.ChargeTransactionDetail];

          let errorMessages: string[] = [];
          for (const currentPaymentMethod of paymentPayloads) {
            if (currentPaymentMethod.PaymentType == "CREDIT_CARD" || currentPaymentMethod.PaymentType == "PVT_LBL_CREDIT_CARD") {
              let cd = chargeDetails.filter((x: any) => x?.PaymentMethod?.PaymentType == currentPaymentMethod.PaymentType && x?.PaymentMethod?.CreditCardType == currentPaymentMethod.CreditCardType && x?.PaymentMethod?.DisplayCreditCardNo == currentPaymentMethod.DisplayCreditCardNo)
              let message = Utils.getErrorIfAny(cd, currentPaymentMethod, currentPaymentMethod.PaymentType);
              if (message && message.length > 0) {
                errorMessages.push(message);
                paymentFailed = true;
              }
            }
            if (currentPaymentMethod.PaymentType == "CREDIT_ON_ACCOUNT") {
              let cd = chargeDetails.filter((x: any) => x?.PaymentMethod?.PaymentType == currentPaymentMethod.PaymentType)
              let message = Utils.getErrorIfAny(cd, currentPaymentMethod, currentPaymentMethod.PaymentType);
              if (message && message.length > 0) {
                errorMessages.push(message);
                paymentFailed = true;
              }
            }
            if (currentPaymentMethod.PaymentType == "GIFT_CARD") {
              let cd = chargeDetails.filter((x: any) => x?.PaymentMethod?.PaymentType == currentPaymentMethod.PaymentType && x?.PaymentMethod?.DisplaySvcNo == currentPaymentMethod.DisplaySvcNo)
              let message = Utils.getErrorIfAny(cd, currentPaymentMethod, currentPaymentMethod.PaymentType);
              if (message && message.length > 0) {
                errorMessages.push(message);
                paymentFailed = true;
              }
            }
            // if we find any check for PaymentTransactionError

            // else show error
          }

          if (paymentFailed) {
            toast.error("Change the Selected Payment Method to Complete the Order", {
              position: "top-center", autoClose: false,
              style: { maxWidth: '100vw', wordBreak: 'break-word', whiteSpace: 'pre-line' }
            })
            if (errorMessages.length > 0) {
              toast.error(errorMessages.join('\n'), {
                position: "top-center", autoClose: 10000,
                style: { maxWidth: '100vw', wordBreak: 'break-word', whiteSpace: 'pre-line' }
              });
            }

            throw new Error("Order completion failed due to payment errors.");
          }
          else {
            const logPayload = Object.keys(selectedCoverageMap).map(primeLineNumber => {
              return Utils.proCovPayload(primeLineNumber, selectedCoverageMap, coverageQuantities, addWarrantyResponse, orderDetails, enterpriseCode, customerID, finalCommissionListPayload);
            });

            OrderManager.saveProCov(logPayload);
            onCloseCompleteOrder();
          }
        }
      }
    } catch (error) {
      console.error('Error during order completion process:', error);
      toast.error("Order completion failed", {
        position: "top-center", autoClose: false,
        style: { maxWidth: '100vw', wordBreak: 'break-word', whiteSpace: 'pre-line' }
      })
    } finally {
      setSubmitting(false);
      dispatch(allActions.actionLoaderActions.hideLoader());
    }
  };

  const renderNewPaymentMethodForm = () => {
    switch (newPaymentMethodType) {
      case 'CREDIT_CARD':
        return (
          <div className={styles['new-card-form']}>
            <StyledTextField
              fullWidth
              name="nameOnCard"
              variant="outlined"
              value={newCardData.nameOnCard}
              onChange={handleNewCardInputChange}
              inputProps={{ maxLength: 25 }}
              margin="dense"
              placeholder="Name on Card"
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <StyledTextField
                name="cardNumber"
                variant="outlined"
                value={newCardData.cardNumber}
                onChange={handleNewCardInputChange}
                inputProps={{
                  inputMode: 'numeric',
                  'data-dd-privacy': 'mask-user-input'
                }}
                margin="dense"
                placeholder="Card Number"
                sx={{ flex: 1 }}
              />
              <StyledTextField
                name="cvv"
                variant="outlined"
                value={newCardData.cvv}
                onChange={handleNewCardInputChange}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 3,
                  'data-dd-privacy': 'mask-user-input'
                }} placeholder={isCvvRequiredForCC ? 'CVV (Required)' : 'CVV'}
                sx={{ display: 'flex', gap: 1, mt: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <StyledTextField
                name="cardType"
                variant="outlined"
                value={cardType}
                InputProps={{
                  readOnly: true,
                  sx: {
                    pointerEvents: 'none',
                    color: 'text.disabled',
                    textAlign: 'center',
                  },
                }}
                margin="dense"
                placeholder="Card Type"
                sx={{ flex: 1 }}
              />
              <StyledTextField
                name="expDate"
                variant="outlined"
                value={newCardData.expDate}
                onChange={handleNewCardInputChange}
                inputProps={{ inputMode: 'numeric', maxLength: 5 }}
                placeholder="Exp. Date (MM/YY)"
                sx={{ display: 'flex', gap: 1, mt: 1 }}

              />
            </Box>
          </div>
        );

      case 'PVT_LBL_CREDIT_CARD':
        const selectedPromo = plccPromos.find(p => p.planCode === currentPromoSelection);
        const selectedPromoText = selectedPromo ? selectedPromo.PromoCodeTextValue : 'Please Select a Promotion';
        return (
          <div className={styles['new-card-form']} style={isMobile ? { padding: '8px', fontSize: '15px' } : {}}>
            <StyledTextField
              fullWidth
              name="nameOnCard"
              variant="outlined"
              value={newCardData.nameOnCard}
              onChange={handleNewCardInputChange}
              inputProps={{ maxLength: 25 }}
              margin="dense"
              placeholder="Name on Card"
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <StyledTextField
                name="cardNumber"
                variant="outlined"
                value={newCardData.cardNumber}
                onChange={handleNewCardInputChange}
                inputProps={{
                  inputMode: 'numeric',
                  'data-dd-privacy': 'mask-user-input'
                }}
                margin="dense"
                placeholder="Card Number"
                sx={{ flex: 1 }}
              />
              <StyledTextField
                name="cvv"
                variant="outlined"
                value={newCardData.cvv}
                onChange={handleNewCardInputChange}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 3,
                  'data-dd-privacy': 'mask-user-input'
                }} placeholder={isCvvRequiredForPLCC ? 'CVV (Required)' : 'CVV'}
                sx={{ display: 'flex', gap: 1, mt: 1 }}
              />
            </Box>
            <StyledTextField
              name="cardType"
              variant="outlined"
              value={cardType}
              InputProps={{
                readOnly: true,
                sx: {
                  pointerEvents: 'none',
                  color: 'text.disabled',
                  textAlign: 'center',
                },
              }}
              margin="dense"
              placeholder="Card Type"
            />

            {plccPromos.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'column' }}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="promo-code-select-label">Promotional Code</InputLabel>
                  <Select
                    labelId="promo-code-select-label"
                    value={currentPromoSelection}
                    label="Promotional Code"
                    onChange={(e) => setCurrentPromoSelection(e.target.value as string)}
                    disabled={isPromoApplied}
                    title={selectedPromoText}
                    sx={{ backgroundColor: '#ffffff', borderRadius: '8px', fontSize: isMobile ? '15px' : undefined }}
                  >
                    <MenuItem value=""><em>Please Select a Promotion</em></MenuItem>
                    {plccPromos.map((promo) => (
                      <MenuItem
                        key={promo.planCode}
                        value={promo.planCode}
                        sx={{ whiteSpace: 'normal' }}
                      >
                        {promo.PromoCodeTextValue}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </div>
        );
      case 'GIFT_CARD':
        return (
          <div className={styles['new-other-payment-form']}>
            <StyledTextField
              fullWidth
              name="displaySvcNo"
              variant="outlined"
              value={newOtherPaymentData.displaySvcNo}
              onChange={handleOtherPaymentInputChange}
              margin="dense"
              inputProps={{
                'data-dd-privacy': 'mask-user-input'
              }}
              placeholder="Gift Card Number"
            />
            <StyledTextField
              fullWidth
              name="paymentReference5"
              variant="outlined"
              value={newOtherPaymentData.paymentReference5}
              onChange={handleOtherPaymentInputChange}
              margin="dense"
              placeholder="PIN"
            />
          </div>
        );
      default:
        return null;
    }
  };

  const renderCoverageOptions = (primeLineNumber: string) => {
    const optionsForCurrentItem = coverageOptions.filter(option => option.primeLineNumber === primeLineNumber);
    const selectedCoverage = selectedCoverageMap[primeLineNumber];

    if (!optionsForCurrentItem || optionsForCurrentItem.length === 0) {
      return (
        <p className={styles['no-coverage-options']}>No Pro-Coverage options available for this item.</p>
      );
    }

    return (
      <div className={styles['coverage-options-list']}>
        {optionsForCurrentItem.map(option => (
          <div
            key={option.ItemID}
            className={`${styles['coverage-option-card']} ${selectedCoverage?.ItemID === option.ItemID ? styles['coverage-option-card-selected'] : ''}`}
            onClick={() => handleCoverageChange(primeLineNumber, option)}
          >
            <div className={styles['coverage-details']}>
              <div className={styles['coverage-item-id']}><b>Item ID:</b> {option.ItemID}</div>
              <div className={styles['coverage-description']}><b>Description:</b> {option.ExtendedDisplayDescription}</div>
              <div className={styles['coverage-cost']}><b>Cost:</b> ${parseFloat(option.UnitPrice).toFixed(2)}</div>
              <div className={styles['coverage-condition']}><b>Condition:</b> {option.WarrantyCondition}</div>
              <div className={styles['coverage-type']}><b>Warranty Type:</b> {option.WarrantyType}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };


  if (!eligibleLines || eligibleLines.length === 0) {
    return (
      <div className={styles.overlay}>
        <div className={styles.container}>
          <div style={{ padding: '50px', textAlign: 'center' }}>
            No items available for PRO Coverage
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.overlay}>
      <div className={styles.container}>
        <Box
          sx={{
            width: '100%',
            padding: '5px 0',
            borderBottom: '1px solid #ccc',
            backgroundColor: '#e0e0e0',
            color: '#242223',
            marginTop: { sm: '2px', md: '2px' }
          }}
        >

          {isMobile && currentStep === 1 && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button className={styles['cancel-button']} onClick={closeProveragePopup}>
                <span className={styles['button-icon']} style={{ fontWeight: 'bold', marginRight: '8px' }}>✕</span>
                Cancel
              </button>
              <button className={styles['next-button']} onClick={handleNextStepClick}>
                Next
                <span className={styles['button-icon']} style={{ fontWeight: 'bold', marginLeft: '8px' }}>→</span>
              </button>
            </div>
          )}

          {isMobile && currentStep === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex' }}>
                <button
                  className={styles['cancel-button']}
                  onClick={closeProveragePopup}
                >
                  <span className={styles['button-icon']} style={{ fontWeight: 'bold', marginRight: '8px' }}>✕</span>
                  Cancel
                </button>
                <button
                  className={styles['summary-button']}
                  onClick={handleBackClick}
                >
                  <span className={styles['button-icon']} style={{ marginRight: '8px' }}>←</span>
                  Back
                </button>
              </div>

              <button
                className={`${styles['complete-order-button']} ${submitting || selectedPaymentMethods.length === 0
                  ? styles['disabled-button']
                  : ''
                  }`}
                onClick={handleCompleteOrder}
                disabled={submitting || selectedPaymentMethods.length === 0}
                style={{ width: '95%' }}
              >
                <span className={styles['button-icon']}>✓</span>
                {submitting ? 'Processing...' : 'Complete Order'}
              </button>
            </div>

          )}
          <Stepper
            activeStep={activeStepMUI}
            alternativeLabel
            sx={{
              marginTop: {
                xs: currentStep === 2 ? '10px' : '10px',
                sm: '20px',
                md: '0px'
              },
              flexWrap: 'wrap',
              '.MuiStepLabel-label': {
                fontSize: { xs: '12px', sm: '14px' },
              },
            }}
          >
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <div className={styles.content}>
          {currentStep === 1 && (
            <>
              {!isMobile && (
                <div className={styles['pro-coverage-type-section']}>
                  <div className={styles['section-title']}>Pro-Coverage Types</div>
                  <p className={styles['note-text']}>
                    Select an item To Apply Pro Coverage
                  </p>
                  <div className={styles['selected-coverages-summary']}>
                    {selectedItems.length > 0 && Object.keys(selectedCoverageMap).length > 0 ? (
                      Object.keys(selectedCoverageMap).map(primeLineNumber => {
                        const item = cartItems.find(i => i.primeLineNumber === primeLineNumber);
                        const coverage = selectedCoverageMap[primeLineNumber];
                        return (
                          <div key={primeLineNumber} className={styles['selected-coverage-item']}>
                            <span className={styles['selected-coverage-text']}>
                              <b>{item?.name || `Item ${primeLineNumber}`}</b>: {coverage.WarrantyType} (${parseFloat(coverage.UnitPrice).toFixed(2)})
                            </span>
                            <button
                              className={styles['remove-coverage-button']}
                              onClick={() => handleRemoveSelectedCoverage(primeLineNumber)}
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })
                    ) : (
                      <p className={styles['no-selected-coverage-text']}>No Pro-Coverage selected yet.</p>
                    )}
                  </div>
                  {selectedItemInLeftPane && (
                    <>
                      {renderCoverageOptions(selectedItemInLeftPane)}
                    </>
                  )}
                </div>
              )}
              <div className={styles['items-selection-section']}>
                <div className={styles['section-title']}>Items Eligible for Pro-Coverage</div>
                <div className={styles['note-text']}>
                  Click an Item to Assign Coverage
                </div>
                {cartItems.filter(item => item.isWarrantyEligible).map(item => {
                  const isAnotherItemSelected = selectedItemInLeftPane !== null && selectedItemInLeftPane !== item.primeLineNumber;
                  const isDisabled = (!item.isItemEligibleForWarranty || item.isWarrantyAlreadyAdded) || isAnotherItemSelected;
                  const quantityEligibleForPC = coverageQuantities[item.primeLineNumber] || item.quantityEligibleForPC;
                  return (
                    <div key={item.primeLineNumber} className={styles['item-group']}>
                      <div
                        className={`${styles['item-card']} ${selectedItemInLeftPane === item.primeLineNumber ? styles['item-card-active'] : ''} ${isDisabled ? styles['item-card-disabled'] : ''}`}
                        onClick={() => !isDisabled && handleItemSelect(item.primeLineNumber)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.primeLineNumber)}
                          onChange={() => { /* Handled by parent div click */ }}
                          disabled={isDisabled}
                          className={styles['item-checkbox']}
                          style={{
                            opacity: isDisabled ? 0.5 : 1
                          }}
                        />
                        <img src={item.imageUrl} alt={item.name} className={styles['item-image']} />
                        <div className={styles['item-details']}>
                          <div className={styles['item-name']}>{item.name}</div>
                          <div className={styles['item-sku-qty']}>SKU # <b>{item.itemId}</b></div>
                          <div className={styles['item-sku-qty']}>Price <b>$ {item.price.toFixed(2)}</b></div>

                          {/* Updated Quantity Display Logic */}
                          <div className={styles['item-sku-qty']}>
                            QTY&nbsp;
                            {selectedItems.includes(item.primeLineNumber) && item.quantityEligibleForPC > 1 ? (
                              // If item is selected AND its max quantity is > 1, show buttons
                              <div className={styles['quantity-selector-inline']}>
                                <button onClick={(e) => { e.stopPropagation(); handleQuantityChange(item.primeLineNumber, -1); }} disabled={quantityEligibleForPC <= 1}>-</button>
                                <span><b>{quantityEligibleForPC}</b></span>
                                <button onClick={(e) => { e.stopPropagation(); handleQuantityChange(item.primeLineNumber, 1); }} disabled={quantityEligibleForPC >= item.quantityEligibleForPC}>+</button>
                              </div>
                            ) : (
                              // Otherwise, show static quantity text
                              <span><b>{quantityEligibleForPC}</b></span>
                            )}
                          </div>

                          {selectedCoverageMap[item.primeLineNumber] && (
                            <div className={styles['coverage-details-wrapper']}>
                              <div
                                className={styles.proCoverageDisplay}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  flexWrap: 'nowrap'
                                }}
                              >
                                <span>
                                  Pro Coverage: <b>{selectedCoverageMap[item.primeLineNumber].WarrantyType}</b>
                                </span>
                                {isMobile && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveSelectedCoverage(item.primeLineNumber);
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: 'red',
                                      cursor: 'pointer',
                                      fontSize: '18px',
                                      fontWeight: 'bold',
                                      padding: '0',
                                      marginLeft: '8px'
                                    }}
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                          {!item.isItemEligibleForWarranty && (
                            <div style={{ color: 'red', fontSize: '12px' }}>Not eligible for warranty</div>
                          )}
                          {item.isWarrantyAlreadyAdded && (
                            <div style={{ color: 'green', fontSize: '12px' }}>Warranty already added</div>
                          )}
                        </div>
                        <div className={styles['button-price-container']}>
                          {selectedCoverageMap[item.primeLineNumber] && (
                            <div className={styles['item-price']}>
                              ${(parseFloat(selectedCoverageMap[item.primeLineNumber].UnitPrice) * quantityEligibleForPC).toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Commission Card - Separate and Readable */}
                      <div className={styles['commission-card']}>
                        <div className={styles['commission-header']}>
                          <div className={styles['commission-title']}>Commissions</div>
                          <button
                            className={styles['update-commission-button']}
                            onClick={() => handleUpdateCommissionClick(item.primeLineNumber)}
                          >
                            Add
                          </button>
                        </div>
                        <div className={styles['commission-content']}>
                          {lineItemCommissions[item.primeLineNumber] && lineItemCommissions[item.primeLineNumber].length > 0 ? (
                            <div className={styles['commission-table']}>
                              <div className={styles['commission-header-row']}>
                                <span className={styles['commission-header-cell']}>Employee</span>
                                <span className={styles['commission-header-cell']}>Number</span>
                                <span className={styles['commission-header-cell']}>Type</span>
                                <span className={styles['commission-header-cell']}>Percentage</span>
                              </div>
                              {lineItemCommissions[item.primeLineNumber].map(commission => (
                                <div key={commission.id} className={styles['commission-row']}>
                                  <span className={styles['commission-cell']}>{commission.employeeName}</span>
                                  <span className={styles['commission-cell']}>{commission.employeeNumber}</span>
                                  <span className={styles['commission-cell']}>{commission.commissionType}</span>
                                  <span className={styles['commission-cell']}>{commission.percentage}%</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className={styles['no-commission-text']}>No commissions assigned</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
                { }
                {/* Bottom buttons for desktop view (Step 1) */}
                {!isMobile && currentStep === 1 && (
                  <div className={styles['next-button-container']}>
                    <button className={styles['cancel-button']} onClick={closeProveragePopup}>
                      <span className={styles['button-icon']} style={{ fontWeight: 'bold', marginRight: '8px' }}>✕</span>
                      Cancel
                    </button>
                    <button className={styles['next-button']} onClick={handleNextStepClick}>
                      Next
                      <span className={styles['button-icon']} style={{ fontWeight: 'bold', marginLeft: '8px' }}>→</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          {currentStep === 2 && (
            <>
              <div className={styles['payment-methods-section']}>
                <div className={styles['section-title']}>Payment Methods</div>
                <div
                  className={styles['add-card-button']}
                  onClick={() => {
                    setShowNewPaymentMethodForm(!showNewPaymentMethodForm);
                    if (!showNewPaymentMethodForm) {
                      setNewPaymentMethodType('');
                    }
                  }}
                >
                  <span style={{ marginRight: '8px' }}>+</span>
                  Add New Payment Method
                </div>
                <div className={styles['new-card-form-container']} style={{ maxHeight: showNewPaymentMethodForm ? '600px' : '0', overflowY: showNewPaymentMethodForm ? 'auto' : 'hidden' }}>
                  {showNewPaymentMethodForm && (
                    <div className={styles['new-payment-method-selection']}>
                      <FormControl fullWidth margin="dense">
                        <InputLabel id="new-payment-method-select-label">Select Payment Type</InputLabel>
                        <Select
                          labelId="new-payment-method-select-label"
                          value={newPaymentMethodType}
                          label="Select Payment Type"
                          onChange={handleNewPaymentMethodTypeChange}
                          sx={{ backgroundColor: '#ffffff', borderRadius: '8px' }}
                        >
                          <MenuItem value="">Please Select</MenuItem>
                          {(showPayments?.CC === '1') && (
                            <MenuItem value="CREDIT_CARD">Credit Card</MenuItem>
                          )}
                          {/* Removed 'CREDIT_ON_ACCOUNT' from new payment method options */}
                          {(showPayments?.GC === '1') && (
                            <MenuItem value="GIFT_CARD">Gift Card</MenuItem>
                          )}
                          {(showPayments?.PLCC === '1') && (
                            <MenuItem value="PVT_LBL_CREDIT_CARD">Private Label Credit Card</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                      {newPaymentMethodType && renderNewPaymentMethodForm()}
                      {newPaymentMethodType && (
                        <button
                          className={styles['save-card-details-button']}
                          onClick={newPaymentMethodType === 'PVT_LBL_CREDIT_CARD' ? handleApplyPromoAndAddPayment : handleAddNewPaymentMethod}
                          disabled={isSaveDisabled}
                          style={{ marginTop: '16px', width: '100%' }}
                        >
                          <span className={styles['button-icon']}>✓</span>
                          {submitting ? 'Processing...' : (
                            newPaymentMethodType === 'PVT_LBL_CREDIT_CARD'
                              ? 'Apply Promo and Add Payment'
                              : 'Add Payment Details'
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className={styles['payment-methods-list']}>
                  {paymentMethods.map((method) => (
                    <div
                      key={method.id}
                      className={`${styles['payment-method-card']} ${selectedPaymentMethods.includes(method.id) ? styles['payment-method-card-selected'] : ''
                        } ${method.isDisabled ? styles['payment-method-card-disabled'] : ''}`}
                      onClick={() => !method.isDisabled && handlePaymentMethodSelect(method.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                      {method.isNew && (
                        <div className={styles['new-tag']}>New</div>
                      )}
                      <input
                        type="checkbox"
                        checked={selectedPaymentMethods.includes(method.id)}
                        onChange={() => handlePaymentMethodSelect(method.id)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={method.isDisabled}
                        className={styles['payment-checkbox']}
                      />
                      <img src={method.iconUrl} alt={method.type} className={styles['payment-method-icon']} />
                      <div className={styles['payment-method-text']}>
                        <span>{method.displayText}</span>
                        {method.promoCode && (
                          <div style={{ marginTop: '4px' }}>
                            <b>Promo: {method.promoCode}</b>
                          </div>
                        )}
                      </div>
                      {selectedPaymentMethods.includes(method.id) && (method.type === 'CREDIT_CARD' || method.type === 'PVT_LBL_CREDIT_CARD') && (
                        <StyledTextField
                          className={styles['payment-amount-input']}
                          type="text"
                          value={existingMethodCvvs[method.id] || ''}
                          onChange={(e) => handleExistingCvvChange(method.id, e.target.value)}
                          label={
                            method.type === 'CREDIT_CARD'
                              ? (isCvvRequiredForCC ? 'CVV (Req)' : 'CVV')
                              : (isCvvRequiredForPLCC ? 'CVV (Req)' : 'CVV')
                          }
                          variant="outlined"
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                          inputProps={{
                            maxLength: 3, style: { width: '100' },
                            'data-dd-privacy': 'mask-user-input'
                          }}
                          sx={{ marginRight: '10px' }} // Extra margin for CVV input
                        />
                      )}
                      {selectedPaymentMethods.includes(method.id) && selectedPaymentMethods.length > 1 && (
                        <StyledTextField
                          className={styles['payment-amount-input']}
                          type="text"
                          value={paymentAmounts[method.id] || ''}
                          onChange={(e) => handlePaymentAmountChange(method.id, e.target.value)}
                          label="Amount"
                          variant="outlined"
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </div>
                  ))}
                </div>


              </div>

              <div className={styles['order-summary-section']}>
                <div className={styles['order-summary-title']}>Order Summary</div>

                {cartItems
                  .filter(item => item.isWarrantyEligible && selectedItems.includes(item.primeLineNumber)) // Only show selected items
                  .map((item) => {
                    const quantityEligibleForPC = coverageQuantities[item.primeLineNumber] || item.quantityEligibleForPC; return (
                      <div key={item.primeLineNumber} className={styles['summary-item-card']}>
                        <div className={styles['item-content-wrapper']}>
                          <img src={item.imageUrl} alt={item.name} className={styles['summary-item-image']} />
                          <div className={styles['summary-item-details']}>
                            <div className={styles['item-name']}>{item.name}</div>
                            {selectedCoverageMap[item.primeLineNumber] && (
                              <div className={styles.proCoverageDisplay}>
                                Pro Coverage: <b>{selectedCoverageMap[item.primeLineNumber]?.WarrantyType || 'N/A'} (QTY: {quantityEligibleForPC})</b>
                              </div>
                            )}
                          </div>
                          {
                            selectedCoverageMap[item.primeLineNumber]?.UnitPrice &&
                            <div className={styles['summary-item-price']}>${(parseFloat(selectedCoverageMap[item.primeLineNumber].UnitPrice) * quantityEligibleForPC).toFixed(2)}</div>
                          }

                        </div>
                        {item.isWarrantyAlreadyAdded && (
                          <div style={{ color: 'green', fontSize: '12px', marginTop: '5px' }}>Warranty already added</div>
                        )}
                        {!item.isItemEligibleForWarranty && (
                          <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>Not eligible for warranty</div>
                        )}
                      </div>
                    )
                  })}

                {orderSummary && (
                  <div className={styles['summary-totals']}>
                    <div className={styles['total-row']}>
                      <span>Sub Total:</span>
                      <span>$ {orderSummary.subTotal.toFixed(2)}</span>
                    </div>
                    <div className={styles['total-row']}>
                      <span>Taxes</span>
                      <span>{orderSummary.taxes > 0 ? `$ ${orderSummary.taxes.toFixed(2)}` : '$ 0.00'}</span>
                    </div>
                    <div className={`${styles['total-row']} ${styles['total-row-large']}`}>
                      <span>Total:</span>
                      <span>$ {orderSummary.total.toFixed(2)}</span>
                    </div>

                  </div>
                )}

                {!isMobile && currentStep === 2 && (
                  <div className={styles['summary-buttons-container']} style={{ display: 'flex', gap: '12px' }}>
                    <button
                      className={styles['summary-button']}
                      onClick={handleBackClick}
                      style={{ maxWidth: '370px', marginLeft: '10px', height: '47px', minWidth: '100px' }}
                    >
                      <span className={styles['button-icon']} style={{ marginRight: '8px', }}>←</span>
                      Back
                    </button>
                    <button
                      className={styles['cancel-button']}
                      onClick={closeProveragePopup}
                      style={{ maxWidth: '370px', marginLeft: '10px', height: '47px' }}
                    >
                      <span className={styles['button-icon']} style={{ fontWeight: 'bold', marginRight: '8px' }}>✕</span>
                      Cancel
                    </button>
                    <button
                      className={`${styles['complete-order-button']} ${submitting || selectedPaymentMethods.length === 0 ? styles['disabled-button'] : ''}`}
                      onClick={handleCompleteOrder}
                      disabled={submitting || selectedPaymentMethods.length === 0}
                      style={{ maxWidth: '370px', marginLeft: '10px', height: '47px' }}
                    >
                      <span className={styles['button-icon']}>✓</span>
                      {submitting ? 'Processing...' : 'Complete Order'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {showCommissionModal && (
        <CommissionModal
          commissions={lineItemCommissions[commissionModalPrimeLineNo] || []}
          onClose={(updatedCommissions) => handleCloseCommissionModal(updatedCommissions, commissionModalPrimeLineNo)}
          orderNo={orderDetails?.OrderNo || ''}
          enterpriseCode={enterpriseCode}
          customerId={orderDetails?.BillToID || ''}
          primeLineNumber={commissionModalPrimeLineNo}
        />
      )}
      {isMobile && isCoverageModalOpen && modalPrimeLineNumber && (
        <div className={`${styles.overlay} ${styles['fade-in']}`} style={{ zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className={styles.container} style={{
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflowY: 'auto',
            position: 'relative',
            margin: 0,
            borderRadius: '15px',
            height: 'fit-content'
          }}>
            <div className={styles['section-title']} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', borderBottom: '1px solid #ccc', background: '#f5f5f5' }}>
              Select Coverage
              <button
                className={styles['remove-coverage-button']}
                onClick={() => {
                  setIsCoverageModalOpen(false);
                  setModalPrimeLineNumber(null);
                }}>
                ✕
              </button>
            </div>

            <div style={{ padding: '10px' }}>
              {renderCoverageOptions(modalPrimeLineNumber)}
            </div>

          </div>
        </div>
      )}
      {commissionConfirmSettings && <NotificationPopup {...commissionConfirmSettings} />}
      {disclosurePopupSettings && <NotificationPopup {...disclosurePopupSettings} />}
      {commissionConfirmSettings && <NotificationPopup {...commissionConfirmSettings} />}
    </div>
  );
};

export default CoveragePopup;